import { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * Sanitize HTML to only allow <mark> tags for search highlighting.
 * This prevents XSS attacks while preserving search term highlighting.
 */
const sanitizeHighlight = (html: string): string => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: ['mark'],
  ALLOWED_ATTR: []
});

interface SearchHighlightProps {
  /**
   * The highlighted text containing HTML <mark> tags from PostgreSQL's ts_headline
   */
  highlightedText?: string;
  /**
   * The fallback plain text to display if no highlighting is available
   */
  plainText: string;
  /**
   * Additional CSS classes to apply to the container
   */
  className?: string;
  /**
   * Maximum number of characters to display before truncating
   */
  maxLength?: number;
  /**
   * Maximum number of lines to display (for summaries)
   */
  maxLines?: number;
  /**
   * Whether to show the full text with highlights applied (for titles and summaries)
   */
  showFullTextWithHighlights?: boolean;
  /**
   * The search query to use for highlighting if no database highlights are available
   */
  searchQuery?: string;
  /**
   * Words that matched via fuzzy search (word_similarity) - for highlighting fuzzy results
   */
  fuzzyMatchedWords?: string[];
}

/**
 * Component for safely rendering search result highlights from PostgreSQL's ts_headline function.
 * Falls back to plain text if no highlighting is available.
 *
 * The highlighting comes with HTML <mark> tags that need to be rendered safely.
 * This component sanitizes the HTML to only allow <mark> tags and applies proper styling.
 */
export function SearchHighlight({
  highlightedText,
  plainText,
  className = '',
  maxLength,
  maxLines,
  showFullTextWithHighlights = false,
  searchQuery,
  fuzzyMatchedWords
}: SearchHighlightProps) {
  const processedText = useMemo(() => {
    // Special handling for titles and summaries - always show full content
    if (showFullTextWithHighlights && plainText) {
      let textToProcess = plainText;

      // For summaries, limit to first 3 lines
      if (maxLines) {
        const lines = textToProcess.split(/\r?\n/);
        if (lines.length > maxLines) {
          textToProcess = lines.slice(0, maxLines).join('\n');
        }
      }

      // Apply truncation by character count if specified
      if (maxLength && textToProcess.length > maxLength) {
        textToProcess = textToProcess.substring(0, maxLength).trim() + '...';
      }

      // Apply highlighting based on database highlights, fuzzy matches, or search query
      const highlightedTerms = new Set<string>();

      // Priority 1: Extract terms from database FTS highlights
      if (highlightedText && highlightedText.includes('<mark>')) {
        const markRegex = /<mark>(.*?)<\/mark>/gi;
        let match;

        while ((match = markRegex.exec(highlightedText)) !== null) {
          const term = match[1];
          if (term) {
            highlightedTerms.add(term);
          }
        }
      }

      // Priority 2: Use fuzzy matched words from database (for fuzzy search results)
      // These are the actual words that matched via word_similarity
      if (highlightedTerms.size === 0 && fuzzyMatchedWords && fuzzyMatchedWords.length > 0) {
        fuzzyMatchedWords.forEach(word => {
          if (word && word.length > 1) {
            highlightedTerms.add(word);
          }
        });
      }

      // Priority 3: Fallback to search query if no other highlights available
      if (highlightedTerms.size === 0 && searchQuery && searchQuery.trim()) {
        // Split search query into individual words
        const queryWords = searchQuery.trim().split(/\s+/);
        queryWords.forEach(word => {
          if (word.length > 1) { // Ignore single characters
            highlightedTerms.add(word);
          }
        });
      }

      // Apply highlights to the full text
      if (highlightedTerms.size > 0) {
        // Create a regex pattern that matches any of the highlighted terms (case-insensitive)
        const termsArray = Array.from(highlightedTerms);
        // Escape special regex characters
        const escapedTerms = termsArray.map(term =>
          term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );

        // Create pattern that will match whole words or partial matches
        const pattern = new RegExp(`(${escapedTerms.join('|')})`, 'gi');

        // Replace matching terms with <mark> tags
        textToProcess = textToProcess.replace(pattern, '<mark>$1</mark>');
      }

      return textToProcess;
    }

    // Original behavior for other use cases (e.g., showing text snippets from search)
    const textToProcess = highlightedText || plainText;

    const finalText = maxLength && textToProcess.length > maxLength
      ? textToProcess.substring(0, maxLength) + '...'
      : textToProcess;

    return finalText;
  }, [highlightedText, plainText, maxLength, maxLines, showFullTextWithHighlights, searchQuery, fuzzyMatchedWords]);

  // If we have processed text with HTML (marks), render it
  if (processedText.includes('<mark>') || highlightedText) {
    return (
      <span
        className={`search-highlight ${className}`}
        dangerouslySetInnerHTML={{
          __html: sanitizeHighlight(processedText)
        }}
        style={{
          // Ensure highlighted terms are styled properly
          '--mark-bg': 'hsl(var(--primary) / 0.2)',
          '--mark-color': 'hsl(var(--primary))'
        } as React.CSSProperties}
      />
    );
  }

  // Fallback to plain text rendering
  return (
    <span className={className}>
      {processedText}
    </span>
  );
}

// CSS styles that should be added to the global stylesheet
export const searchHighlightStyles = `
.search-highlight mark {
  background-color: var(--mark-bg, hsl(var(--primary) / 0.3));
  color: var(--mark-color, hsl(var(--primary)));
  font-weight: 900;
  padding: 0 0.125rem;
  border-radius: 0.125rem;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
}
`;